import { Checkbox, Form, Input } from '~/shared/components/form';

import {
    Container,
    Content,
    StyledCTAButton,
    StyledForgotPasswordLink,
    StyledMiddleContent,
} from '../styled';

import { LoginInputs } from '../models/fields';
import { UseFormReturn } from 'react-hook-form';
import { LoginDrawerMode } from '../hooks/useLoginDrawerState';
import { useLocalStorage } from '~/shared/hooks/useLocalStorage';
import { TranslationKey } from '~/lib';
import { useBasket } from '~/features/basket';
import Spinner from '~/shared/components/Spinner/Spinner';

type LoginViewProps = {
    methods: UseFormReturn<LoginInputs>;
    handleLoginSubmit: (payload: LoginInputs) => Promise<void>;
    authenticationError: string;
    renderAuthenticationError: (errorMessage: string) => JSX.Element;
    setLoginDrawerMode: (mode: LoginDrawerMode) => void;
    translate: (key: TranslationKey) => string;
    isValidatingLogin: boolean;
};

export const LoginView = ({
    methods,
    handleLoginSubmit,
    authenticationError,
    renderAuthenticationError,
    setLoginDrawerMode,
    translate,
    isValidatingLogin,
}: LoginViewProps) => {
    const { isInitialized } = useBasket();
    const [shouldRememberMe, setShouldRememberMe] = useLocalStorage('rememberMe', false);
    return (
        <>
            <Content>
                <Form methods={methods} onSubmit={handleLoginSubmit}>
                    <Container>
                        <Input
                            data-testid="login-email"
                            name="email"
                            autoComplete={shouldRememberMe ? 'email' : undefined}
                            placeholder={translate('login.email')}
                            required
                        />
                        <Input
                            data-testid="login-password"
                            type="password"
                            name="password"
                            autoComplete={shouldRememberMe ? 'current-password' : 'new-password'}
                            id="password"
                            placeholder={translate('login.password')}
                            required
                            noValidation
                        />
                    </Container>

                    <StyledMiddleContent>
                        <Checkbox
                            name={translate('login.rememberMe')}
                            label={translate('login.rememberMe')}
                            checked={shouldRememberMe}
                            onChange={() => {
                                setShouldRememberMe(!shouldRememberMe);
                            }}
                        />
                        <StyledForgotPasswordLink
                            data-testid="forgot-password-link"
                            onClick={() => setLoginDrawerMode(LoginDrawerMode.FORGOT_PASSWORD)}
                            onKeyDown={(e: React.KeyboardEvent) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setLoginDrawerMode(LoginDrawerMode.FORGOT_PASSWORD);
                                }
                            }}
                            tabIndex={0}
                        >
                            {translate('login.forgotPassword')}
                        </StyledForgotPasswordLink>
                    </StyledMiddleContent>

                    <StyledCTAButton
                        data-testid="login"
                        type="submit"
                        disabled={!isInitialized}
                        variant="primary"
                    >
                        {isValidatingLogin ? <Spinner /> : translate('login.login')}
                    </StyledCTAButton>

                    {authenticationError && renderAuthenticationError(authenticationError)}
                </Form>
            </Content>
        </>
    );
};
